import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useGetAuthoringComponentQuery, useGetAuthoringComponentRevisionQuery } from '../api/authoring'
import { useIntent, shown } from '../api/useIntent'
import { problemMessage } from '../api/baseApi'
import type { LinkPath } from '../model/link'
import type { PinState } from '../model/link'
import { Loading, QueryError } from './QueryState'

/** Which edge a pin is being set on, and what names it on the node. */
export type PinnedEdge = { kind: 'child'; linkId: string } | { kind: 'reference'; edgeId: string }

/**
 * Move, set or release the pin on one edge.
 *
 * A pin is the coordinate `(id, revision)`. Publication now freezes the
 * whole tree, so this narrower control is framed as a hold: it keeps one
 * edge on an older target revision during future captures. Unpinned links
 * inside that target still follow whichever revisions capture sees.
 *
 * The control is deliberately per edge, because that is what the model
 * addresses: the same component linked twice is two placements and they
 * pin independently.
 *
 * **Both edges pin, and this draws both.** A child and a reference take
 * the same coordinate against the same target and differ in two things —
 * the intent's name and the member that names the edge — so `edge` is a
 * discriminator rather than a second dialog. Choosing a revision, reading
 * one back to show what is about to be pinned, and saying what a pin does
 * not do are identical work either way.
 */
export function PinDialog({
  tenant,
  open,
  /** The component holding the placement — a pin is arranged by the holder. */
  commandId,
  /** The link path to the holding node within that component. */
  commandPath,
  edge,
  /** The target, whose revisions are being chosen among. */
  targetId,
  pin,
  onClose,
}: {
  tenant: string
  open: boolean
  commandId: string
  commandPath: LinkPath
  /** Which edge is being pinned, and what names it on this node. */
  edge: PinnedEdge
  targetId: string
  pin: PinState | undefined
  onClose: () => void
}) {
  const intents = edge.kind === 'child'
    ? { pin: 'component.child_pinned', unpin: 'component.child_unpinned', member: 'linkId', id: edge.linkId }
    : { pin: 'component.reference_pinned', unpin: 'component.reference_unpinned', member: 'edgeId', id: edge.edgeId }

  const target = useGetAuthoringComponentQuery({ tenant, id: targetId }, { skip: !open })
  const { send, isLoading: sending, error: sendError } = useIntent(tenant)

  const head = target.data?.revision
  const pinnedTo = pin?.kind === 'pinned' ? pin.revision : undefined
  const [choice, setChoice] = useState<number | ''>('')

  // Open on what is pinned now, or on the head when nothing is — the
  // revision an author is most likely to want is the one they can see.
  useEffect(() => {
    if (open) setChoice(pinnedTo ?? head ?? '')
  }, [open, pinnedTo, head])

  // What that revision actually is. There is no history listing (B11),
  // so this reads one revision at a time — enough to tell an author what
  // they are about to pin to, and not enough to show them a history.
  const preview = useGetAuthoringComponentRevisionQuery(
    { tenant, id: targetId, revision: typeof choice === 'number' ? choice : 1 },
    { skip: !open || typeof choice !== 'number' },
  )

  const revisions = head ? Array.from({ length: head }, (_, index) => head - index) : []
  const unchanged = choice === pinnedTo

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        Hold <code>{intents.id}</code> at a revision
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              → {targetId}
            </Typography>
            {pin?.kind === 'pinned' ? (
              <Chip size="small" color="primary" variant="outlined" label={`held r${pin.revision}`} />
            ) : (
              <Chip size="small" variant="outlined" label="follows current" />
            )}
          </Stack>

          {target.isLoading && <Loading label="Reading the target…" />}
          {target.error && <QueryError error={target.error} title="Could not read the target" />}
          {sendError && <Alert severity="error">{problemMessage(sendError, 'The intent was refused.')}</Alert>}

          {head !== undefined && (
            <>
              <TextField
                select
                label="Hold at revision"
                value={choice}
                onChange={(event) => setChoice(Number(event.target.value))}
                disabled={sending}
                helperText={`This component stands at r${head}. Revisions are contiguous from 1.`}
              >
                {revisions.map((revision) => (
                  <MenuItem key={revision} value={revision}>
                    r{revision}
                    {revision === head ? ' — current' : ''}
                    {revision === pinnedTo ? ' — held now' : ''}
                  </MenuItem>
                ))}
              </TextField>

              {preview.data && (
                <Stack spacing={0.25}>
                  <Typography variant="caption" color="text.secondary">
                    At r{typeof choice === 'number' ? choice : ''}
                  </Typography>
                  <Typography variant="body2">
                    {preview.data.label ?? preview.data.id}
                  </Typography>
                  {preview.data.updatedAt && (
                    <Typography variant="caption" color="text.secondary">
                      {new Date(preview.data.updatedAt).toLocaleString()}
                      {preview.data.updatedBy ? ` · ${preview.data.updatedBy}` : ''}
                    </Typography>
                  )}
                </Stack>
              )}

              <Alert severity="info" icon={false}>
                This holds <strong>this {edge.kind === 'child' ? 'placement' : 'reference'}</strong> at that revision
                of <code>{targetId}</code> when future publications are captured. Links <em>inside</em> that document
                keep following the revisions each publication captures.
              </Alert>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {pinnedTo !== undefined && (
          <Button
            color="inherit"
            disabled={sending}
            onClick={() =>
              send(commandId, intents.unpin, { [intents.member]: intents.id }, commandPath).then(onClose).catch(shown)
            }
          >
            Release hold
          </Button>
        )}
        <Button
          variant="contained"
          disabled={sending || typeof choice !== 'number' || unchanged}
          onClick={() =>
            send(commandId, intents.pin, { [intents.member]: intents.id, revision: choice }, commandPath).then(
              onClose,
            ).catch(shown)
          }
        >
          {pinnedTo === undefined ? 'Hold revision' : 'Move hold'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
